import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useShortlist } from "@/context/ShortlistContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, Upload, FileText, X, ChevronLeft, ChevronRight, Pencil } from "lucide-react";

const ROLES = ["Producer", "Casting Director", "Creative / Advertising Agency", "Production Company / Studio", "Brand / Company", "Game Developer", "Filmmaker", "Content Creator", "Author / Publisher", "Independent Production", "Other"];

const PRODUCTION_TYPES = ["Commercial", "Online video", "Corporate", "E-learning", "Telephony", "VR / Gaming", "Other"];
const USAGE_RELEVANT = ["Commercial", "Online video"];
const SCRIPT_LENGTHS = [
  "10–60 seconds / 10–65 words",
  "1–5 minutes / 130–650 words",
  "5–10 minutes / 650–1,300 words",
  "More than 10 minutes / more than 1,300 words",
  "Other",
];
const TERRITORIES = ["Netherlands", "Belgium", "Benelux", "Europe", "Worldwide", "Other"];
const USAGE_PERIODS = ["3 months", "6 months", "1 year", "2 years", "Perpetual", "Other"];
const GENDERS = ["Boy", "Girl", "No preference"];

// Commercial-only usage rights collection
const USAGE_MEDIA_GROUPS = [
  {
    group: "Digital & Social",
    options: [
      { label: "Organic social media / digital", desc: "Unpaid content published on the Client’s and/or third-party websites, social media channels and digital platforms." },
      { label: "Paid social media", desc: "Paid, sponsored, boosted or promoted advertising on social media platforms." },
      { label: "Online video / pre-roll advertising", desc: "Paid video advertising, including pre-roll, mid-roll, post-roll and other paid online video placements." },
      { label: "Streaming / CTV / OTT advertising", desc: "Paid advertising on streaming and connected-TV/OTT platforms, including services such as Hulu, Netflix, Spotify and similar platforms." },
      { label: "Mobile & tablet applications", desc: "Use within mobile or tablet applications." },
      { label: "Podcasts", desc: "Use within podcasts, including branded or sponsored podcast content." },
    ],
  },
  {
    group: "Broadcast & Theatrical",
    options: [
      { label: "TV broadcast", desc: "Traditional linear television, including terrestrial, cable and satellite TV." },
      { label: "Radio broadcast", desc: "Traditional terrestrial, satellite or digital radio broadcast." },
      { label: "Cinema / theatrical", desc: "Cinema advertising, trailers or other theatrical exhibition." },
    ],
  },
  {
    group: "Retail, Public & Events",
    options: [
      { label: "Point of Sale (POS) / In-store", desc: "Retail locations, shops, showrooms, displays, kiosks and other point-of-sale environments." },
      { label: "Digital Out-of-Home (DOOH)", desc: "Digital billboards, screens and other digital advertising in public spaces." },
      { label: "Events / exhibitions / trade shows", desc: "Live events, conferences, exhibitions, trade shows, presentations or installations." },
    ],
  },
  {
    group: "Other",
    options: [
      { label: "Internal / corporate use", desc: "Internal company communications, presentations, training or other non-public corporate use." },
      { label: "Other", desc: "" },
    ],
  },
];
const COMM_TERRITORIES = ["Local / Regional", "National", "Multiple countries", "Worldwide / Global"];
const COMM_TERRITORY_DETAIL = {
  "Local / Regional": "Please specify region/market",
  "National": "Country",
  "Multiple countries": "Please specify countries/territories",
};
const COMM_PERIODS = ["3 months", "6 months", "12 months", "24 months", "In perpetuity", "Other"];
const slugify = (s) => s.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "");

const STEP_LABELS = ["Project", "Usage", "Script", "Casting", "Your details"];
const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

export default function BookingForm({ talents = [], initialSelectedIds = null, initialChildName = "" }) {
  const { items: favorites, clear } = useShortlist();
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialSelectedIds ?? favorites.map((t) => t.id)));
  const toggleTalent = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const chosen = favorites.filter((t) => selectedIds.has(t.id));
  // Favorites load from localStorage after first render; pre-check them once (unless the
  // caller passed an explicit initial selection, e.g. the Favorites page).
  const didInitSel = useRef(initialSelectedIds != null);
  useEffect(() => {
    if (!didInitSel.current && favorites.length) {
      setSelectedIds(new Set(favorites.map((t) => t.id)));
      didInitSel.current = true;
    }
  }, [favorites]);
  const [form, setForm] = useState({
    project_type: "", script_length: "", script_length_other: "", deadline: "",
    languages: "", country_accent: "", gender_pref: "", voice_age: "", style: "",
    usage_territory: "", usage_territory_other: "", usage_period: "", usage_period_other: "",
    usage_media: [], usage_media_other: "",
    comm_territory: "", comm_territory_detail: "", comm_period: "", comm_period_other: "",
    script_mode: "paste", script: "", script_provided_later: false,
    script_file_url: "", script_file_name: "", instructions: "",
    child_name: initialChildName, remote_direction: "", company: "", role: "", confirm_casting: false,
    first_name: "", last_name: "", email: "", phone: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const usageRelevant = USAGE_RELEVANT.includes(form.project_type);
  const isCommercial = form.project_type === "Commercial";
  const toggleMedia = (label) => setForm((f) => ({
    ...f,
    usage_media: f.usage_media.includes(label) ? f.usage_media.filter((x) => x !== label) : [...f.usage_media, label],
  }));
  const mediaLabels = () => form.usage_media.map((m) => (m === "Other" && form.usage_media_other.trim() ? `Other: ${form.usage_media_other.trim()}` : m));
  const territoryDisplay = () => {
    if (!usageRelevant) return "N/A";
    if (isCommercial) return form.comm_territory + (form.comm_territory_detail.trim() ? ` — ${form.comm_territory_detail.trim()}` : "");
    return form.usage_territory === "Other" ? form.usage_territory_other : form.usage_territory;
  };
  const periodDisplay = () => {
    if (!usageRelevant) return "N/A";
    if (isCommercial) return form.comm_period === "Other" ? form.comm_period_other : form.comm_period;
    return form.usage_period === "Other" ? form.usage_period_other : form.usage_period;
  };

  const flow = useMemo(
    () => ["project", ...(usageRelevant ? ["usage"] : []), "script", "casting", "details", "review"],
    [usageRelevant]
  );
  const [stepKey, setStepKey] = useState("project");
  const step = flow.includes(stepKey) ? stepKey : "project";
  const idx = flow.indexOf(step);

  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const topRef = useRef(null);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goto = (k) => { setStepKey(k); scrollTop(); };
  const next = () => { if (validate(step)) goto(flow[idx + 1]); };
  const back = () => goto(flow[Math.max(0, idx - 1)]);

  function validate(s) {
    if (s === "project") {
      if (!form.company.trim()) return toast.error("Please enter your company / organisation."), false;
      if (!form.role) return toast.error("Please select your role."), false;
      if (!form.project_type) return toast.error("Please choose a production type."), false;
      if (!form.script_length) return toast.error("Please choose a script length."), false;
      if (form.script_length === "Other" && !form.script_length_other.trim())
        return toast.error("Please specify the script length."), false;
      if (!form.languages.trim()) return toast.error("Please enter the required language(s)."), false;
    }
    if (s === "usage") {
      if (isCommercial) {
        if (form.usage_media.length === 0) return toast.error("Please select at least one usage option."), false;
        if (form.usage_media.includes("Other") && !form.usage_media_other.trim())
          return toast.error("Please specify the usage."), false;
        if (!form.comm_territory) return toast.error("Please select a territory."), false;
        if (form.comm_territory !== "Worldwide / Global" && !form.comm_territory_detail.trim())
          return toast.error("Please complete the territory details."), false;
        if (!form.comm_period) return toast.error("Please select a usage period."), false;
        if (form.comm_period === "Other" && !form.comm_period_other.trim())
          return toast.error("Please specify the usage period."), false;
      } else {
        if (!form.usage_territory) return toast.error("Please choose where the recording will be used."), false;
        if (form.usage_territory === "Other" && !form.usage_territory_other.trim())
          return toast.error("Please specify the territory."), false;
        if (!form.usage_period) return toast.error("Please choose the usage duration."), false;
        if (form.usage_period === "Other" && !form.usage_period_other.trim())
          return toast.error("Please specify the usage duration."), false;
      }
    }
    if (s === "details") {
      if (!form.first_name.trim() || !form.last_name.trim())
        return toast.error("Please enter your first and last name."), false;
      if (!emailOk(form.email)) return toast.error("Please enter a valid email address."), false;
    }
    return true;
  }

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/casting/upload-script", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, script_file_url: data.url, script_file_name: data.filename, script_provided_later: false }));
      toast.success("Script uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try a PDF, DOC, DOCX or TXT (max 15MB).");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!validate("project") || (usageRelevant && !validate("usage")) || !validate("details")) return;
    if (!form.confirm_casting) return toast.error("Please confirm this is a casting/booking enquiry.");
    setSending(true);
    const shortlistNames = chosen.map((t) => t.name);
    const territoryVal = territoryDisplay();
    const periodVal = periodDisplay();
    const mediaArr = isCommercial ? mediaLabels() : [];
    try {
      await api.post("/casting", {
        name: `${form.first_name} ${form.last_name}`.trim(),
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
        company: form.company, role: form.role,
        project_type: form.project_type,
        languages: form.languages, country_accent: form.country_accent,
        gender_pref: form.gender_pref, voice_age: form.voice_age, style: form.style,
        script_length: form.script_length, script_length_other: form.script_length_other,
        deadline: form.deadline,
        usage_territory: territoryVal, usage_period: periodVal, usage_media: mediaArr,
        remote_direction: form.remote_direction === "Yes",
        script: form.script_mode === "paste" ? form.script : "",
        script_file_url: form.script_mode === "file" ? form.script_file_url : "",
        script_file_name: form.script_mode === "file" ? form.script_file_name : "",
        script_provided_later: form.script_mode === "later",
        instructions: form.instructions,
        child_name: form.child_name,
        selected_talent_ids: chosen.map((t) => t.id),
        selected_talent_names: shortlistNames,
      });
      if (window.gtag) window.gtag("event", "booking_request_submitted");
      setDone(true);
      if (talents.length) clear();
    } catch (err) {
      toast.error("Something went wrong. Please email bookings@voiceoverkids.agency");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div ref={topRef} data-testid="booking-success" className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center md:p-12">
        <CheckCircle2 className="mx-auto mb-3 text-green-600" size={44} />
        <h3 className="font-heading text-2xl font-bold text-zinc-900">Your booking request has been received</h3>
        <p className="mx-auto mt-3 max-w-md text-zinc-600">
          We’ll check the availability of the selected voice talent(s) and contact you regarding the price and final booking confirmation.
        </p>
      </div>
    );
  }

  return (
    <div ref={topRef} data-testid="booking-form">
      {/* Progress indicator */}
      <ol className="mb-8 flex items-center gap-1.5" data-testid="booking-progress">
        {STEP_LABELS.map((label) => {
          const key = label === "Your details" ? "details" : label.toLowerCase();
          const inFlow = flow.includes(key);
          const active = step === key;
          const complete = inFlow && flow.indexOf(key) < idx;
          return (
            <li key={label} className="flex flex-1 flex-col items-start">
              <div className={`h-1.5 w-full rounded-full ${active ? "bg-brand-violet" : complete ? "bg-red-300" : "bg-zinc-200"}`} />
              <span className={`mt-2 text-[11px] font-semibold uppercase tracking-wide sm:text-xs ${active ? "text-brand-violet" : complete ? "text-zinc-600" : "text-zinc-400"}`}>
                {label}{!inFlow && key === "usage" ? " · n/a" : ""}
              </span>
            </li>
          );
        })}
      </ol>

      {/* STEP: Project */}
      {step === "project" && (
        <div className="space-y-6" data-testid="step-project">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Company / Organisation *">
              <Input data-testid="company" className="h-11 rounded-xl" placeholder="Company, agency, studio, production or project name"
                value={form.company} onChange={(e) => set("company", e.target.value)} />
            </Field>
            <Field label="Your role *">
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="role"><SelectValue placeholder="Select your role" /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Group label="Do you already have a specific child / voice talent in mind? (optional)">
            <Input data-testid="child-name" className="h-11 w-full rounded-xl"
              placeholder="Listed talent only — or leave blank for talent suggestions"
              value={form.child_name} onChange={(e) => set("child_name", e.target.value)} />
          </Group>
          <Group label="What type of production is this?">
            <RadioCards name="ptype" columns={2} value={form.project_type} onChange={(v) => set("project_type", v)} options={PRODUCTION_TYPES} testid="ptype" />
          </Group>
          <Group label="Which language(s) do you need? *">
            <Input data-testid="languages" className="h-11 w-full rounded-xl" placeholder="e.g. Dutch, English, German"
              value={form.languages} onChange={(e) => set("languages", e.target.value)} />
          </Group>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Country / accent (optional)">
              <Input data-testid="country-accent" className="h-11 rounded-xl" placeholder="e.g. Netherlands, US, British"
                value={form.country_accent} onChange={(e) => set("country_accent", e.target.value)} />
            </Field>
            <Field label="Voice age (optional)">
              <Input data-testid="voice-age" className="h-11 rounded-xl" placeholder="e.g. sounds around 6–8 years old"
                value={form.voice_age} onChange={(e) => set("voice_age", e.target.value)} />
            </Field>
          </div>
          <Group label="Gender (optional)">
            <RadioCards name="gender" columns={2} value={form.gender_pref} onChange={(v) => set("gender_pref", v)} options={GENDERS} testid="gender" />
          </Group>
          <Group label="Voice style / tone (optional)">
            <Input data-testid="style" className="h-11 w-full rounded-xl" placeholder="e.g. natural, energetic, warm, conversational, playful"
              value={form.style} onChange={(e) => set("style", e.target.value)} />
          </Group>
          <Group label="How long is the script?">
            <RadioCards name="slen" value={form.script_length} onChange={(v) => set("script_length", v)} options={SCRIPT_LENGTHS} testid="slen" />
            {form.script_length === "Other" && (
              <Input data-testid="slen-other" className="mt-2 h-11 rounded-xl" placeholder="Please specify the length"
                value={form.script_length_other} onChange={(e) => set("script_length_other", e.target.value)} />
            )}
          </Group>
          <Group label="Preferred delivery date (optional)">
            <Input data-testid="deadline" type="date" className="h-11 w-full rounded-xl sm:w-64"
              value={form.deadline} onChange={(e) => set("deadline", e.target.value)} />
          </Group>
        </div>
      )}

      {/* STEP: Usage */}
      {step === "usage" && (
        <div className="space-y-6" data-testid="step-usage">
          {isCommercial ? (
            <>
              <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-600">
                This information helps us determine the appropriate usage rights and quote.
              </p>
              <Group label="Usage *">
                <p className="-mt-1 mb-3 text-sm text-zinc-600">
                  Where will the voice-over be used? Select all that apply.
                </p>
                <div className="space-y-5" data-testid="usage-media">
                  {USAGE_MEDIA_GROUPS.map((g) => (
                    <div key={g.group}>
                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-500">{g.group}</p>
                      <div className="grid gap-2">
                        {g.options.map((o) => {
                          const on = form.usage_media.includes(o.label);
                          return (
                            <label key={o.label} data-testid={`media-${slugify(o.label)}`}
                              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition ${on ? "border-brand-violet bg-red-50 ring-1 ring-brand-violet" : "border-zinc-200 hover:border-zinc-300"}`}>
                              <Checkbox checked={on} onCheckedChange={() => toggleMedia(o.label)} className="mt-0.5" data-testid={`media-check-${slugify(o.label)}`} />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-zinc-800">{o.label}</span>
                                {o.desc && <span className="mt-0.5 block text-xs leading-snug text-zinc-500">{o.desc}</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {form.usage_media.includes("Other") && (
                  <Input data-testid="media-other-detail" className="mt-3 h-11 rounded-xl" placeholder="Please specify usage"
                    value={form.usage_media_other} onChange={(e) => set("usage_media_other", e.target.value)} />
                )}
              </Group>
              <Group label="Territory *">
                <p className="-mt-1 mb-3 text-sm text-zinc-600">Where will the content be used?</p>
                <RadioCards name="commterr" columns={2} value={form.comm_territory}
                  onChange={(v) => setForm((f) => ({ ...f, comm_territory: v, comm_territory_detail: "" }))}
                  options={COMM_TERRITORIES} testid="commterr" />
                {COMM_TERRITORY_DETAIL[form.comm_territory] && (
                  <Input data-testid="commterr-detail" className="mt-2 h-11 rounded-xl"
                    placeholder={COMM_TERRITORY_DETAIL[form.comm_territory]}
                    value={form.comm_territory_detail} onChange={(e) => set("comm_territory_detail", e.target.value)} />
                )}
              </Group>
              <Group label="Usage period *">
                <p className="-mt-1 mb-3 text-sm text-zinc-600">How long will the voice-over be used?</p>
                <RadioCards name="commperiod" columns={2} value={form.comm_period} onChange={(v) => set("comm_period", v)} options={COMM_PERIODS} testid="commperiod" />
                {form.comm_period === "Other" && (
                  <Input data-testid="commperiod-other-detail" className="mt-2 h-11 rounded-xl" placeholder="Please specify usage period"
                    value={form.comm_period_other} onChange={(e) => set("comm_period_other", e.target.value)} />
                )}
              </Group>
            </>
          ) : (
            <>
              <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-600">
                We use this information to calculate any applicable usage rights / buyout fees.
              </p>
              <Group label="Where will the recording be used?">
                <RadioCards name="terr" columns={2} value={form.usage_territory} onChange={(v) => set("usage_territory", v)} options={TERRITORIES} testid="terr" />
                {form.usage_territory === "Other" && (
                  <Input data-testid="terr-other" className="mt-2 h-11 rounded-xl" placeholder="Please specify the territory"
                    value={form.usage_territory_other} onChange={(e) => set("usage_territory_other", e.target.value)} />
                )}
              </Group>
              <Group label="How long will the recording be used?">
                <RadioCards name="period" columns={2} value={form.usage_period} onChange={(v) => set("usage_period", v)} options={USAGE_PERIODS} testid="period" />
                {form.usage_period === "Other" && (
                  <Input data-testid="period-other" className="mt-2 h-11 rounded-xl" placeholder="Please specify the usage duration"
                    value={form.usage_period_other} onChange={(e) => set("usage_period_other", e.target.value)} />
                )}
              </Group>
            </>
          )}
        </div>
      )}

      {/* STEP: Casting / recording details */}
      {step === "casting" && (
        <div className="space-y-6" data-testid="step-casting">
          <Group label="Is remote direction required during the recording? (optional)">
            <RadioCards name="remote" columns={2} value={form.remote_direction} onChange={(v) => set("remote_direction", v)} options={["Yes", "No"]} testid="remote" />
          </Group>
          <Group label="Which of your favorite voices should we consider? (optional)">
            {favorites.length === 0 ? (
              <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-600">
                You haven't added any favorite voices yet. <Link to="/child-voices" className="font-semibold text-brand-violet hover:underline">Browse voices</Link> and tap the heart to add them — they'll appear here to choose from.
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2" data-testid="shortlist-chooser">
                {favorites.map((t) => {
                  const on = selectedIds.has(t.id);
                  return (
                    <label key={t.id} data-testid={`booking-talent-${t.id}`}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-2.5 transition ${on ? "border-brand-violet bg-red-50/40 ring-1 ring-brand-violet" : "border-zinc-200 hover:border-zinc-300"}`}>
                      <Checkbox checked={on} onCheckedChange={() => toggleTalent(t.id)} data-testid={`booking-talent-check-${t.id}`} />
                      <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-zinc-200">
                        {t.photo_url && <img src={mediaUrl(t.photo_url)} alt={t.name} className="h-full w-full object-cover" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-zinc-800">{t.name}</span>
                        <span className="block truncate text-xs text-zinc-500">{t.language}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </Group>
        </div>
      )}

      {/* STEP: Script */}
      {step === "script" && (
        <div className="space-y-6" data-testid="step-script">
          <Group label="Script (optional)">
            <div className="mb-3 flex flex-wrap gap-2">
              {[["paste", "Paste script"], ["file", "Upload a file"], ["later", "I’ll provide it later"]].map(([m, l]) => (
                <button key={m} type="button" data-testid={`script-mode-${m}`} onClick={() => set("script_mode", m)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${form.script_mode === m ? "border-brand-violet bg-red-50 text-brand-violet" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
                  {l}
                </button>
              ))}
            </div>
            {form.script_mode === "paste" && (
              <Textarea data-testid="script-paste" rows={6} className="rounded-xl" placeholder="Paste your script here…"
                value={form.script} onChange={(e) => set("script", e.target.value)} />
            )}
            {form.script_mode === "file" && (
              <div>
                {form.script_file_url ? (
                  <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
                    <FileText size={20} className="text-brand-violet" />
                    <span className="flex-1 truncate text-sm font-medium text-zinc-800">{form.script_file_name}</span>
                    <button type="button" onClick={() => set("script_file_url", "")} className="text-zinc-400 hover:text-red-500" aria-label="Remove file"><X size={18} /></button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 hover:border-brand-violet hover:bg-red-50/40">
                    {uploading ? <Loader2 className="animate-spin text-brand-violet" size={22} /> : <Upload size={22} className="text-brand-violet" />}
                    <span className="font-semibold text-zinc-700">{uploading ? "Uploading…" : "Click to upload"}</span>
                    <span>PDF, DOC, DOCX, TXT or RTF · max 15MB</span>
                    <input ref={fileRef} data-testid="script-file" type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.rtf,.odt" onChange={onFile} disabled={uploading} />
                  </label>
                )}
              </div>
            )}
            {form.script_mode === "later" && (
              <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-600">No problem — you can send the final script once it’s ready.</p>
            )}
          </Group>
          <Group label="Instructions for the voice talent">
            <Textarea data-testid="instructions" rows={4} className="rounded-xl"
              placeholder="For example: tone of voice, pronunciation, references, or any other recording instructions."
              value={form.instructions} onChange={(e) => set("instructions", e.target.value)} />
          </Group>
        </div>
      )}

      {/* STEP: Your details */}
      {step === "details" && (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="step-details">
          <Field label="First name *"><Input data-testid="first-name" className="h-11 rounded-xl" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
          <Field label="Last name *"><Input data-testid="last-name" className="h-11 rounded-xl" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
          <Field label="Email address *"><Input data-testid="email" type="email" className="h-11 rounded-xl" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Phone number"><Input data-testid="phone" className="h-11 rounded-xl" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        </div>
      )}

      {/* STEP: Review */}
      {step === "review" && (
        <div className="space-y-4" data-testid="step-review">
          <h3 className="font-heading text-xl font-bold text-zinc-900">Your booking request</h3>
          <Summary label="Shortlisted / preferred voices" onEdit={() => goto("casting")}
            value={chosen.map((t) => t.name).join(", ") || "General casting request"} />
          {form.child_name && <Summary label="Child / voice talent name" onEdit={() => goto("project")} value={form.child_name} />}
          <Summary label="Production type" onEdit={() => goto("project")} value={form.project_type} />
          <Summary label="Language(s)" onEdit={() => goto("project")} value={form.languages} />
          {form.country_accent && <Summary label="Country / accent" onEdit={() => goto("project")} value={form.country_accent} />}
          {form.gender_pref && <Summary label="Gender" onEdit={() => goto("project")} value={form.gender_pref} />}
          {form.voice_age && <Summary label="Voice age" onEdit={() => goto("project")} value={form.voice_age} />}
          {form.style && <Summary label="Voice style / tone" onEdit={() => goto("project")} value={form.style} />}
          <Summary label="Script length" onEdit={() => goto("project")} value={form.script_length === "Other" ? form.script_length_other : form.script_length} />
          {form.deadline && <Summary label="Preferred delivery date" onEdit={() => goto("project")} value={form.deadline} />}
          {isCommercial && <Summary label="Usage" onEdit={() => goto("usage")} value={mediaLabels().join(", ")} />}
          <Summary label="Usage territory" onEdit={usageRelevant ? () => goto("usage") : null} value={territoryDisplay()} />
          <Summary label="Usage duration" onEdit={usageRelevant ? () => goto("usage") : null} value={periodDisplay()} />
          {form.remote_direction && <Summary label="Remote direction" onEdit={() => goto("casting")} value={form.remote_direction} />}
          <Summary label="Script" onEdit={() => goto("script")}
            value={form.script_mode === "file" ? (form.script_file_name || "—") : form.script_mode === "later" ? "Provided later" : (form.script ? "Pasted text" : "—")} />
          {form.instructions && <Summary label="Instructions" onEdit={() => goto("script")} value={form.instructions} />}
          <Summary label="Your details" onEdit={() => goto("details")} value={`${form.first_name} ${form.last_name}${form.company ? " · " + form.company : ""} · ${form.email}${form.phone ? " · " + form.phone : ""}`} />
          {form.role && <Summary label="Role" onEdit={() => goto("project")} value={form.role} />}

          <label data-testid="confirm-casting" className="mt-2 flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
            <Checkbox checked={form.confirm_casting} onCheckedChange={(v) => set("confirm_casting", !!v)} className="mt-0.5" data-testid="confirm-casting-check" />
            <span className="text-sm text-zinc-700">
              I confirm that this is a casting / booking enquiry for a production and not an application to become voice talent. <span className="text-brand-violet">*</span>
              <span className="mt-1 block text-xs text-zinc-500">Want to join our roster? Please <Link to="/join-our-talent" className="font-semibold text-brand-violet hover:underline">view our talent application requirements</Link> instead.</span>
            </span>
          </label>

          <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="booking-disclaimer">
            This is not yet a confirmed booking. We will check the availability of the selected voice talent(s) and then contact you with the price and final confirmation.
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-8 flex items-center justify-between gap-3">
        {idx > 0 ? (
          <button type="button" onClick={back} data-testid="booking-back"
            className="inline-flex items-center gap-1 rounded-full border border-zinc-300 px-5 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50">
            <ChevronLeft size={16} /> Back
          </button>
        ) : <span />}
        {step !== "review" ? (
          <button type="button" onClick={next} data-testid="booking-next"
            className="inline-flex items-center gap-1 rounded-full bg-brand-violet px-7 py-3 text-sm font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover">
            Continue <ChevronRight size={16} />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={sending || !form.confirm_casting} data-testid="booking-submit"
            className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-7 py-3 text-sm font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover disabled:opacity-60">
            {sending && <Loader2 size={16} className="animate-spin" />} Submit booking request
          </button>
        )}
      </div>
    </div>
  );
}

function RadioCards({ name, value, onChange, options, columns = 1, testid }) {
  return (
    <div role="radiogroup" className={`grid gap-2 ${columns === 2 ? "sm:grid-cols-2" : ""}`}>
      {options.map((o) => (
        <label key={o} data-testid={`${testid}-${o.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
          className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm transition ${value === o ? "border-brand-violet bg-red-50 ring-1 ring-brand-violet" : "border-zinc-200 hover:border-zinc-300"}`}>
          <input type="radio" name={name} checked={value === o} onChange={() => onChange(o)} className="h-4 w-4 accent-[#E11D2A]" />
          <span className="font-medium text-zinc-800">{o}</span>
        </label>
      ))}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <h4 className="mb-2.5 font-heading text-base font-bold text-zinc-900">{label}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="label-eyebrow text-zinc-500">{label}</Label>{children}</div>;
}

function Summary({ label, value, onEdit }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-zinc-100 py-2.5">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{label}</p>
        <p className="mt-0.5 break-words text-sm font-medium text-zinc-800">{value || "—"}</p>
      </div>
      {onEdit && (
        <button type="button" onClick={onEdit} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brand-violet hover:underline">
          <Pencil size={12} /> Edit
        </button>
      )}
    </div>
  );
}
